import React, {useEffect, useMemo} from 'react';
import {Box, ClickAwayListener, Popper, Stack} from '@mui/material';
import {
  designSystemToken,
  Headline,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import {ROUTES} from '@contentlab/config/routesConstants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import {Project, ProjectStatus} from '@contentlab/types/project';
import {ProjectStatusUpdate} from '@contentlab/types/projectStatusUpdate';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import {getContentLabProjectShelfLastOpenedTimestamp} from '@/utils/LtidStorage';
import translate from '@/utils/translate';
import Conditional from '@/components/conditional';
import useProjectsQuery from '@/hooks/content-studio/use-projects-query';
import useProjectsStatusQuery from '@/hooks/content-studio/use-projects-status-query';
import useNavigation from '@/hooks/use-navigation';
import {scrollbarMixins} from '@/styles/mixins';
import EmptyState from './EmptyState';
import ProjectGroupSkeleton from './ProjectGroupSkeleton';
import ProjectsGroup from './ProjectsGroup';

interface ProjectWithStatus {
  project: Project;
  projectStatus: ProjectStatusUpdate;
}

function ProjectShelf({
  anchorEl,
  onClose,
  presentationId,
  onBadgeCountUpdate,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  presentationId: string;
  onBadgeCountUpdate?: (count: number) => void;
}) {
  const {navigate} = useNavigation();
  const {uiInteraction} = useUiInteractionEvent();
  const {data: projectStatuses, isLoading} = useProjectsStatusQuery({
    updatedHoursAgo: 24,
  });
  const {data: projects} = useProjectsQuery({
    sortBy: 'created_at',
    sortOrder: 'desc',
    live: false,
    pageSize: 100,
  });

  // Group projects by status
  const projectsWithStatus: ProjectWithStatus[] = useMemo(
    () =>
      (
        projectStatuses
          .map((projectStatus) => {
            const foundProject = projects?.find(
              (project) => project.id === projectStatus.projectId
            );
            if (!foundProject) {
              return null;
            }
            return {project: foundProject, projectStatus};
          })
          .filter(Boolean) as ProjectWithStatus[]
      )
        .slice()
        .sort(
          (a: ProjectWithStatus, b: ProjectWithStatus) =>
            new Date(b.project.updatedAt).getTime() -
            new Date(a.project.updatedAt).getTime()
        ),
    [projectStatuses, projects]
  );

  // Calculate badge count for ready projects
  const badgeCount = useMemo(() => {
    const lastOpenedTimestamp =
      getContentLabProjectShelfLastOpenedTimestamp()?.getTime() ?? 0;
    return projectsWithStatus.filter(({projectStatus, project}) => {
      return (
        projectStatus.status === ProjectStatus.READY &&
        new Date(project.updatedAt).getTime() > lastOpenedTimestamp
      );
    }).length;
  }, [projectsWithStatus]);

  // Update badge count when it changes
  useEffect(() => {
    if (onBadgeCountUpdate) {
      onBadgeCountUpdate(badgeCount);
    }
  }, [badgeCount, onBadgeCountUpdate]);

  // TODO - once product decide what to do with ProjectStatus.PENDING_USER_ACTION projects - implement filtering for them
  const inProgressProjects = projectsWithStatus.filter(({projectStatus}) =>
    [ProjectStatus.IN_PROGRESS].includes(projectStatus.status as ProjectStatus)
  );

  const doneProjects = projectsWithStatus.filter(({projectStatus}) =>
    [ProjectStatus.READY, ProjectStatus.FAILED].includes(
      projectStatus.status as ProjectStatus
    )
  );

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Popper
        open={!!anchorEl}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={{zIndex: 5}}
      >
        <Stack
          sx={{
            width: '470px',
            backgroundColor: designSystemToken('semantic.bg.secondary'),
            borderRadius: '16px',
            paddingInline: '16px',
            boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.1)',
            marginTop: '16px',
            maxHeight: '500px',
            overflow: 'auto',
          }}
          direction="column"
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            padding="12px 4px 12px 0"
            borderBottom={`1px solid ${designSystemToken(
              'semantic.bg.tertiary'
            )}`}
          >
            <Headline size="md" flex={1}>
              {translate('Recent Projects')}
            </Headline>
            <Box
              onClick={() => {
                onClose();
              }}
              sx={{cursor: 'pointer'}}
            >
              <Icon
                name="Actions-Close-Normal"
                appearance="primary"
                size="medium"
              />
            </Box>
          </Stack>
          <Stack
            overflow="auto"
            sx={{...scrollbarMixins.overrideScrollbarHidden()}}
          >
            <Conditional
              condition={
                !isLoading &&
                inProgressProjects.length + doneProjects.length === 0
              }
            >
              <EmptyState />
            </Conditional>
            <Conditional
              condition={!isLoading}
              fallback={<ProjectGroupSkeleton rowsCount={4} />}
            >
              <ProjectsGroup
                projects={projectsWithStatus}
                presentationId={presentationId}
              />
            </Conditional>
          </Stack>
          <Stack
            paddingY="12px"
            sx={{cursor: 'pointer'}}
            onClick={() => {
              navigate(`${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PROJECTS}`);
              uiInteraction({
                item_name: 'See all results',
                interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECTS_SHELF,
                presentation_id: presentationId,
              });
            }}
          >
            <Stack
              gap="8px"
              flexDirection="row"
              alignItems="center"
              sx={{cursor: 'pointer'}}
            >
              <Icon
                name="Navigation-Creations"
                appearance="primary"
                size="medium"
                style={{paddingTop: '8px', paddingBottom: '8px'}}
              />
              <Label size="lg" color={designSystemToken('semantic.fg.primary')}>
                {translate('See all results')}
              </Label>
            </Stack>
          </Stack>
        </Stack>
      </Popper>
    </ClickAwayListener>
  );
}

export default ProjectShelf;
